import { z } from 'zod';
import { BENEFIT_KINDS } from './benefits.js';

/** مخططات المزايا الثابتة. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

const optionalDateOnly = dateOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

const money = z.coerce.number().min(0).max(99_999_999_999.99);

/* ==========================================================================
   الخطّة
   ========================================================================== */

/**
 * خطّةُ ميزة.
 *
 * وحصّةُ الموظّف لا تتجاوز ما تدفعه الشركة: حصّةٌ أكبرُ تعني أنّ الشركةَ
 * تربح من تأمين موظّفيها — وهو رقمٌ يُكتب سهوًا ولا يُكتشف إلّا في قسيمة.
 */
export const UpsertBenefitPlanSchema = z
  .object({
    name: z.string().trim().min(2, 'اسم الخطّة قصير.').max(160),
    kind: z.enum(BENEFIT_KINDS).default('ALLOWANCE'),
    amount: money,
    employeeShare: money.default(0),
    provider: optionalText(160),
    notes: optionalText(2000),
    isActive: z.boolean().default(true),
  })
  .refine((v) => v.kind !== 'MEDICAL' || v.employeeShare <= v.amount, {
    message: 'حصّةُ الموظّف أكبرُ ممّا تدفعه الشركة.',
    path: ['employeeShare'],
  })
  .refine((v) => v.kind === 'MEDICAL' || v.employeeShare === 0, {
    message: 'الحصّةُ للتأمين الطبّيّ وحدَه.',
    path: ['employeeShare'],
  });
export type UpsertBenefitPlanInput = z.infer<typeof UpsertBenefitPlanSchema>;

export const BenefitPlansQuerySchema = z.object({
  kind: z.enum(BENEFIT_KINDS).optional(),
  /** الموقوفةُ تُخفى افتراضًا — تُطلب صراحةً. */
  all: z.enum(['1']).optional(),
});
export type BenefitPlansQuery = z.infer<typeof BenefitPlansQuerySchema>;

/* ==========================================================================
   الاشتراك
   ========================================================================== */

export const EnrollBenefitSchema = z
  .object({
    employeeIds: z
      .array(Cuid)
      .min(1, 'اختر موظّفًا واحدًا على الأقلّ.')
      .max(200, 'المشتركون المطلوبون أكثر من مئتين.'),
    startsOn: dateOnly,
    amount: money.nullable().optional(),
    employeeShare: money.nullable().optional(),
    notes: optionalText(1000),
  })
  .refine(
    (v) =>
      v.amount === null ||
      v.amount === undefined ||
      v.employeeShare === null ||
      v.employeeShare === undefined ||
      v.employeeShare <= v.amount,
    {
      message: 'حصّةُ الموظّف أكبرُ ممّا تدفعه الشركة.',
      path: ['employeeShare'],
    },
  );
export type EnrollBenefitInput = z.infer<typeof EnrollBenefitSchema>;

export const UpdateEnrollmentSchema = z.object({
  amount: money.nullable().optional(),
  employeeShare: money.nullable().optional(),
  notes: optionalText(1000),
});
export type UpdateEnrollmentInput = z.infer<typeof UpdateEnrollmentSchema>;

/**
 * إنهاءُ اشتراك.
 *
 * بشهرٍ يُكتب لا بحذف: قسيمةُ الشهر الماضي صُرفت بهذا البدل، وحذفُ السطر
 * يجعل من يراجعها لا يعرف من أين جاء الرقم.
 */
export const EndEnrollmentSchema = z.object({
  endsOn: dateOnly,
});
export type EndEnrollmentInput = z.infer<typeof EndEnrollmentSchema>;

export const BenefitEnrollmentsQuerySchema = z.object({
  planId: Cuid.optional(),
  employeeId: Cuid.optional(),
  /** الجاريةُ وحدَها — ما لم يُطلب غيرُها. */
  all: z.enum(['1']).optional(),
});
export type BenefitEnrollmentsQuery = z.infer<
  typeof BenefitEnrollmentsQuerySchema
>;
